import {useCallback, useEffect, useState} from 'react';

import {FormField} from 'shells/form/form-field';
import {FormWrapperWithButtons} from 'shells/form/form-wrapper-with-buttons';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import {TextField} from 'shells/textfield/textfield';

import {useUpdateSharedEmailAddress} from '../graphql/hooks/use-update-shared-email-address';

import styles from './shared-email-address-modal.module.css';

interface Props {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly addressId: string;
    readonly currentSenderName: string;
}

export function EditSenderNameModal(props: Props) {
    const [senderName, setSenderName] = useState<string>(props.currentSenderName);

    const {updateSharedEmailAddress, isLoading, error} = useUpdateSharedEmailAddress();

    const resetFields = useCallback(() => {
        setSenderName(props.currentSenderName);
    }, [props.currentSenderName]);

    useEffect(() => {
        if (props.isOpen) {
            setSenderName(props.currentSenderName);
        }
    }, [props.isOpen, props.currentSenderName]);

    const handleClose = () => {
        resetFields();
        props.onClose();
    };

    const handleSubmit = async () => {
        const trimmedSenderName = senderName.trim();
        if (!trimmedSenderName) {
            return;
        }

        const {data} = await updateSharedEmailAddress({
            id: props.addressId,
            displayName: trimmedSenderName,
        });

        if (data?.sharedEmailAddressUpdate.fromAddress) {
            handleClose();
        }
    };

    if (!props.isOpen) {
        return null;
    }

    return (
        <ModalLightbox onClose={handleClose}>
            <div className={styles.container}>
                <FormWrapperWithButtons
                    title='Edit sender name'
                    isSaving={isLoading}
                    onCancel={handleClose}
                    onSaveClick={handleSubmit}
                    buttonText='Save'
                    savingText='Saving…'
                    saveButtonDisabled={!senderName.trim().length || isLoading}
                    resetOnSaveComplete={true}
                    errorMessage={error ?? undefined}
                    errorSaving={Boolean(error)}
                >
                    <div className='full-width'>
                        <FormField innerId='edit-sender-name' label='Sender name'>
                            <TextField
                                value={senderName}
                                onChange={setSenderName}
                                focusOnMount={true}
                                onEnter={handleSubmit}
                            />
                        </FormField>
                    </div>
                </FormWrapperWithButtons>
            </div>
        </ModalLightbox>
    );
}
